import { useCallback, useEffect, useState } from 'react';
import {
  isAuthenticated,
  type CalendarEvent,
  type MailRule,
  type MeetAccess,
  type Session,
} from 'zitimail-shared-types';

import { api } from './admin/api';
import { fetchSession } from './api/session';
import { ForgotPassword, recoveryRoute, ResetPassword } from './auth/Recover';
import { CalendarView } from './calendar/CalendarView';
import { EventReminders } from './calendar/EventReminders';
import {
  CalendarIcon,
  ChatIcon,
  ContactsIcon,
  FilesIcon,
  GearIcon,
  MailIcon,
  MeetIcon,
  ShieldIcon,
  UsersIcon,
} from './components/icons';
import { AccountMenu, initialsOf, LOOK_LABEL } from './components/AccountMenu';
import { PANE_LABEL, PANES, setLayout, useLayout } from './mail/layout';
import { ToastProvider } from './components/Toasts';
import { ContactsView } from './contacts/ContactsView';
import { FilesView } from './files/Files';
import { EDiscoveryView } from './mail/EDiscovery';
import { MailView } from './mail/MailView';
import { ruleFromSender } from './mail/Rules';
import { RecoverDeleted } from './mail/Recover';
import { Settings } from './mail/Settings';
import { TenantAdmin } from './mail/TenantAdmin';
import { TwoStepReminder, twoStepOutcome } from './mail/TwoStep';
import './mail/mail.css';
import './calendar/calendar.css';
import { LOOKS, useTheme, type Look, type Mode, type Theme } from './theme';
import { setUndoSeconds, UNDO_CHOICES, undoSeconds, type UndoSeconds } from './mail/undoSend';

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; session: Session }
  | { status: 'error'; message: string };

export function App(): JSX.Element {
  const { theme, setLook, setMode } = useTheme();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    fetchSession(controller.signal)
      .then((session) => setState({ status: 'ready', session }))
      .catch((error: unknown) => {
        // An aborted request is an unmount, not a failure to report.
        if (controller.signal.aborted) {
          return;
        }
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'unknown error',
        });
      });

    return () => controller.abort();
  }, [reloads]);

  // An API call that finds the session gone sends the user back to sign-in.
  useEffect(() => {
    const ended = () => setReloads((n) => n + 1);
    window.addEventListener('genworx:session-ended', ended);
    return () => window.removeEventListener('genworx:session-ended', ended);
  }, []);

  const signOut = useCallback(async () => {
    await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
    setState({ status: 'loading' });
    setReloads((n) => n + 1);
  }, []);

  // A forgotten password works signed in or not: the link may be opened anywhere.
  const recovery = recoveryRoute(window.location.pathname);
  if (recovery) {
    return recovery.page === 'forgot' ? (
      <ForgotPassword />
    ) : (
      <ResetPassword token={recovery.token} />
    );
  }
  if (state.status === 'loading') {
    return <Loading />;
  }
  if (state.status === 'error') {
    return <Failure message={state.message} />;
  }
  if (!isAuthenticated(state.session)) {
    return <SignIn theme={theme} setLook={setLook} setMode={setMode} />;
  }
  return (
    <ToastProvider>
      <Shell
        session={state.session}
        theme={theme}
        setLook={setLook}
        setMode={setMode}
        onSignOut={signOut}
      />
    </ToastProvider>
  );
}

function Loading(): JSX.Element {
  return (
    <div className="state" aria-busy="true">
      <p>Loading…</p>
    </div>
  );
}

function Failure({ message }: { message: string }): JSX.Element {
  return (
    <div className="state">
      <div className="alert" role="alert">
        <strong>Could not reach the mail service</strong>
        {message}
      </div>
    </div>
  );
}

const SIGN_IN_PROBLEMS: Record<string, string> = {
  'unknown-domain':
    'That address is not hosted on ZitiMail. Check the spelling, or ask your administrator.',
  'bad-address': 'That does not look like an email address.',
  'need-address': 'Enter your email address to sign in.',
  failed: 'Sign-in did not complete. Try again.',
  expired: 'That sign-in had expired, or was finished in another browser or tab. Sign in again.',
};

/** The domain of a typed address, once it plausibly has one. */
function domainOf(address: string): string | undefined {
  const at = address.lastIndexOf('@');
  const domain =
    at < 0
      ? ''
      : address
          .slice(at + 1)
          .trim()
          .toLowerCase();
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain) ? domain : undefined;
}

/**
 * Signed out. The address decides where the user signs in: the BFF finds the
 * organisation that owns its domain and sends them to that organisation's own
 * sign-in — their directory, their MFA. The page says so as they type.
 */
function SignIn({
  theme,
  setLook,
  setMode,
}: {
  theme: Theme;
  setLook: (look: Look) => void;
  setMode: (mode: Mode) => void;
}): JSX.Element {
  const problem = new URLSearchParams(window.location.search).get('login');
  const [address, setAddress] = useState('');
  const domain = domainOf(address);
  return (
    <div className="auth">
      <header className="auth-top">
        {/* On a light plate: the logo's dark teal would vanish on the navy. */}
        <span className="auth-logo">
          <img src="/brand/genworx-logo.png" alt="Genworx" width="131" height="24" />
        </span>
        <span className="auth-brand-name">Cloud</span>
      </header>

      <section className="auth-hero">
        <h1>
          Ziti<span className="auth-hero-accent">Mail</span>
        </h1>
        <p className="auth-tagline">Secure. Connected. Productive.</p>
        <p className="auth-hero-lede">
          Enterprise email powered by Genworx Unified SSO, designed for modern teams.
        </p>
        <ul className="auth-features">
          <li>
            <span className="auth-feature-icon" aria-hidden="true">
              <ShieldIcon />
            </span>
            <span>
              <strong>Unified SSO</strong>
              One identity across all Genworx services
            </span>
          </li>
          <li>
            <span className="auth-feature-icon" aria-hidden="true">
              <MailIcon />
            </span>
            <span>
              <strong>Secure &amp; private</strong>
              Encrypted to and from your devices, hosted on Genworx Cloud
            </span>
          </li>
          <li>
            <span className="auth-feature-icon" aria-hidden="true">
              <UsersIcon />
            </span>
            <span>
              <strong>Work anywhere</strong>
              Webmail, the desktop app, and the mail apps on your phone
            </span>
          </li>
        </ul>
      </section>

      {/* From Edmund's mock; decorative. Replace with a full-resolution asset
          when one exists. */}
      <img className="auth-art" src="/brand/hero-envelope.webp" alt="" width="530" height="440" />

      <main className="auth-card">
        <div className="auth-card-head">
          <span className="auth-card-icon" aria-hidden="true">
            <MailIcon />
          </span>
          <div>
            <p className="auth-card-name">
              Ziti<span className="auth-hero-accent">Mail</span>
            </p>
            <p className="auth-card-sub">Powered by Genworx Unified SSO</p>
          </div>
        </div>
        <h2>Sign in with your Genworx account</h2>
        <p className="auth-lede">Access your email and all Genworx services with one identity.</p>
        {problem && SIGN_IN_PROBLEMS[problem] && (
          <div className="alert" role="alert">
            {SIGN_IN_PROBLEMS[problem]}
          </div>
        )}
        {/* A navigation, not a form submission: /auth/login redirects to the
            identity provider on another origin, and the page's CSP
            (form-action 'self') covers a form's redirects too. */}
        <form
          className="auth-form"
          method="get"
          action="/auth/login"
          onSubmit={(event) => {
            event.preventDefault();
            const email = new FormData(event.currentTarget).get('email');
            window.location.assign(
              `/auth/login?email=${encodeURIComponent(typeof email === 'string' ? email : '')}`,
            );
          }}
        >
          <label className="auth-label" htmlFor="auth-email">
            Email address
          </label>
          <div className="auth-input">
            <MailIcon />
            <input
              id="auth-email"
              type="email"
              name="email"
              autoComplete="username"
              placeholder="you@company.com"
              aria-describedby="auth-route"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              required
              autoFocus
            />
          </div>
          <p id="auth-route" className="auth-route" aria-live="polite">
            {domain ? (
              <>
                Continues to the sign-in page for <span className="auth-domain">{domain}</span>
              </>
            ) : (
              '\u00a0'
            )}
          </p>
          <button className="btn" type="submit">
            Continue with SSO
          </button>
        </form>
        <a className="auth-forgot" href="/forgot-password">
          Forgot your password?
        </a>
        <p className="auth-secure">
          <ShieldIcon />
          Your connection is secure and encrypted
        </p>
      </main>

      <footer className="auth-foot">
        <span>&copy; {new Date().getFullYear()} Genworx Pte Ltd</span>
        <Prefs theme={theme} setLook={setLook} setMode={setMode} />
      </footer>
    </div>
  );
}

type View =
  'mail' | 'settings' | 'recover' | 'calendar' | 'contacts' | 'files' | 'admin' | 'ediscovery';

/**
 * ZitiMeet's page for the organisation, when Genworx has turned it on
 * (migration 0050); null while unknown or off. The address is ZitiMeet's,
 * never one made here.
 */
function useMeetUrl(tenant: string): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    api<MeetAccess>(`/tenants/${encodeURIComponent(tenant)}/meet`, { signal: controller.signal })
      .then((meet) => setUrl(meet.enabled ? meet.url : null))
      // Not knowing is the same as off: the button stays disabled.
      .catch(() => setUrl(null));
    return () => controller.abort();
  }, [tenant]);
  return url;
}

function Shell({
  session,
  theme,
  setLook,
  setMode,
  onSignOut,
}: {
  session: Extract<Session, { authenticated: true }>;
  theme: Theme;
  setLook: (look: Look) => void;
  setMode: (mode: Mode) => void;
  onSignOut: () => void;
}): JSX.Element {
  const { displayName, email, tenant, roles } = session.user;
  const initials = initialsOf(displayName || email);
  // Back from Keycloak's two-step page (ADR-0058): Settings, which says how it went.
  const [view, setView] = useState<View>(() => (twoStepOutcome() ? 'settings' : 'mail'));
  // An address chosen in Contacts to write to; a fresh object each time so
  // writing to the same person twice opens compose twice.
  const [writeTo, setWriteTo] = useState<{ address: string } | undefined>();
  // A rule made from a message, handed to Settings to finish and save.
  const [newRule, setNewRule] = useState<MailRule | undefined>();
  // An event a reminder opened; a fresh object each time, as writeTo.
  const [shown, setShown] = useState<{ event: CalendarEvent } | undefined>();
  const openEvent = useCallback((event: CalendarEvent) => {
    setShown({ event });
    setView('calendar');
  }, []);
  const isAdmin = roles.includes('tenant-admin');
  // Granted on its own (ADR-0021): neither implies the other.
  const canDiscover = roles.includes('ediscovery');
  const meetUrl = useMeetUrl(tenant);

  return (
    <div className="app">
      <header className="chrome">
        <span className="brand">
          {/* On a light plate, as on the sign-in page: the logo's dark teal
              would vanish on a dark header. */}
          <span className="brand-logo">
            <img src="/brand/genworx-logo.png" alt="Genworx" width="98" height="18" />
          </span>
          ZitiMail
        </span>
        <span className="tenant">{tenant}</span>
        <span className="spacer" />
        <AccountMenu
          name={displayName || email}
          email={email}
          initials={initials}
          theme={theme}
          setLook={setLook}
          setMode={setMode}
          onSignOut={onSignOut}
        >
          <div className="account-row">
            <span>Reading pane</span>
            <ReadingPaneChoice />
          </div>
          <div className="account-row">
            <span>Undo send</span>
            <UndoSendChoice />
          </div>
        </AccountMenu>
      </header>

      <div className={`panes${view === 'mail' ? '' : ' panes-wide'}`}>
        <nav className="rail" aria-label="Genworx Workspace">
          <button
            type="button"
            className={view === 'mail' ? 'on' : ''}
            onClick={() => setView('mail')}
          >
            <MailIcon />
            Mail
          </button>
          <button
            type="button"
            className={view === 'calendar' ? 'on' : ''}
            onClick={() => setView('calendar')}
          >
            <CalendarIcon />
            Calendar
          </button>
          <button
            type="button"
            className={view === 'contacts' ? 'on' : ''}
            onClick={() => setView('contacts')}
          >
            <ContactsIcon />
            Contacts
          </button>
          {/* Cloud storage: Seafile, Nextcloud, ownCloud (ADR-0049). */}
          <button
            type="button"
            className={view === 'files' ? 'on' : ''}
            onClick={() => setView('files')}
          >
            <FilesIcon />
            Files
          </button>
          {/* The rest of the suite shares this realm; it is visible from day
              one so the product reads as a suite. ZitiMeet opens in its own
              tab once Genworx has turned it on for the organisation; Chat has
              nothing behind it yet. */}
          {meetUrl ? (
            <button
              type="button"
              onClick={() => window.open(meetUrl, '_blank', 'noopener,noreferrer')}
            >
              <MeetIcon />
              Meet
            </button>
          ) : (
            <button type="button" disabled title="Not enabled for your organisation">
              <MeetIcon />
              Meet
            </button>
          )}
          <button type="button" disabled>
            <ChatIcon />
            Chat
          </button>
          <span className="spacer" />
          <button
            type="button"
            className={view === 'settings' ? 'on' : ''}
            onClick={() => setView('settings')}
            title="Signature, out of office, rules, forwarding, blocked senders, mail apps…"
          >
            <GearIcon />
            Settings
          </button>
          {canDiscover && (
            <button
              type="button"
              className={view === 'ediscovery' ? 'on' : ''}
              onClick={() => setView('ediscovery')}
              title="Search and export your organisation's mail archive"
            >
              eDiscovery
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className={view === 'admin' ? 'on' : ''}
              onClick={() => setView('admin')}
              title="Your organisation's mail administration"
            >
              Admin
            </button>
          )}
        </nav>

        {view === 'mail' && (
          <MailView
            email={email}
            name={displayName || undefined}
            tenant={tenant}
            onSettings={() => {
              setNewRule(undefined);
              setView('settings');
            }}
            onRecover={() => setView('recover')}
            onMakeRule={(sender) => {
              setNewRule(ruleFromSender(sender));
              setView('settings');
            }}
            writeTo={writeTo?.address}
          />
        )}
        {view === 'calendar' && <CalendarView email={email} show={shown?.event} />}
        {view === 'contacts' && (
          <ContactsView
            onWrite={(address) => {
              setWriteTo({ address });
              setView('mail');
            }}
          />
        )}
        {view === 'files' && <FilesView slug={tenant} />}
        {view === 'settings' && (
          <Settings tenant={tenant} newRule={newRule} onBack={() => setView('mail')} />
        )}
        {view === 'recover' && <RecoverDeleted onBack={() => setView('mail')} />}
        {view === 'admin' && isAdmin && (
          <TenantAdmin slug={tenant} me={email} onBack={() => setView('mail')} />
        )}
        {view === 'ediscovery' && canDiscover && (
          <EDiscoveryView slug={tenant} onBack={() => setView('mail')} />
        )}
      </div>
      <EventReminders me={email} onOpen={openEvent} />
      <TwoStepReminder
        tenant={tenant}
        email={email}
        onOpen={() => {
          setNewRule(undefined);
          setView('settings');
        }}
      />
    </div>
  );
}

function Prefs({
  theme,
  setLook,
  setMode,
}: {
  theme: Theme;
  setLook: (look: Look) => void;
  setMode: (mode: Mode) => void;
}): JSX.Element {
  return (
    <div className="prefs">
      <div className="seg" role="group" aria-label="Direction">
        {LOOKS.map((look) => (
          <button
            type="button"
            key={look}
            aria-pressed={theme.look === look}
            onClick={() => setLook(look)}
          >
            {LOOK_LABEL[look]}
          </button>
        ))}
      </div>
      <div className="seg" role="group" aria-label="Theme">
        {(['light', 'dark'] as const).map((mode) => (
          <button
            type="button"
            key={mode}
            aria-pressed={theme.mode === mode}
            onClick={() => setMode(mode)}
          >
            {mode === 'light' ? '☀' : '☾'}
          </button>
        ))}
      </div>
    </div>
  );
}

/** How long Send waits for an Undo (mail/undoSend.ts). */
function ReadingPaneChoice(): JSX.Element {
  const { pane } = useLayout();
  return (
    <div className="seg" role="group" aria-label="Reading pane">
      {PANES.map((p) => (
        <button
          type="button"
          key={p}
          aria-pressed={pane === p}
          title={
            p === 'off'
              ? 'No reading pane: an opened message takes the list’s place'
              : `Reading pane ${p === 'right' ? 'beside' : 'below'} the message list`
          }
          onClick={() => setLayout({ pane: p })}
        >
          {PANE_LABEL[p]}
        </button>
      ))}
    </div>
  );
}

function UndoSendChoice(): JSX.Element {
  const [seconds, setSeconds] = useState<UndoSeconds>(undoSeconds);
  return (
    <div className="seg" role="group" aria-label="Undo send">
      {UNDO_CHOICES.map((s) => (
        <button
          type="button"
          key={s}
          aria-pressed={seconds === s}
          title={s === 0 ? 'Send at once, no Undo' : `Undo for ${s} seconds after Send`}
          onClick={() => {
            setUndoSeconds(s);
            setSeconds(s);
          }}
        >
          {s === 0 ? 'No undo' : `${s}s`}
        </button>
      ))}
    </div>
  );
}
